import { Box, Stack, type Theme, Typography, useTheme } from "@mui/material";
// import CrownIcon from "assets/crown.svg";
import { Avatar, AvatarGroup } from "components/avatar";
import { Popover } from "components/popover";
import { useGlobalStore } from "controller";
import { ScheduledTicketModal } from "pages/modules/control-center/recurring-tickets/components/ScheduledTicketModal";
import { Fragment, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { trackAiHome } from "../../analytics";
import { mspInstant } from "../../store";
import type {
  CalendarEvent,
  CalendarEventPerson,
  CalendarEventPriority,
} from "../../types";

/** Colour of the priority flag shown next to the ticket id. */
const getPriorityColor = (
  priority: CalendarEventPriority,
  theme: Theme,
): string => {
  switch (priority) {
    case "critical":
      return theme.palette.error.main;
    case "high":
      return theme.palette.warning.main;
    case "medium":
      return theme.palette.info.main;
    default:
      return theme.palette.lightBg.low as string;
  }
};

/**
 * Colour of the vertical bar on the left of the card — keyed off the event
 * type, so a ticket's bar mirrors its priority flag and a recurring ticket
 * reads as high priority.
 */
const getEventTypeColor = (event: CalendarEvent, theme: Theme): string => {
  switch (event.eventType) {
    case "meeting":
      return theme.palette.success.main;
    case "activity":
      return theme.palette.primary.main;
    case "recurring-ticket":
      return getPriorityColor("high", theme);
    default:
      return getPriorityColor(event.priority ?? "low", theme);
  }
};

const MAX_VISIBLE_ASSIGNEES = 3;

/**
 * Attendees only read as attendees on a meeting — on every other event type the
 * resource is the single owning tech, which the card doesn't surface.
 */
const getAttendees = (event: CalendarEvent) =>
  event.eventType === "meeting" ? event.assignees : undefined;

const AssigneeAvatars = ({
  assignees,
}: {
  assignees: CalendarEventPerson[];
}) => (
  // Inline-flex so the group sits in the meta text line rather than breaking it.
  <Box sx={{ ml: "6px", display: "inline-flex", verticalAlign: "middle" }}>
    <AvatarGroup
      max={MAX_VISIBLE_ASSIGNEES}
      childWidthHeight="18px"
      overlapMargin="-6px"
      stackOrder="last"
    >
      {assignees.map((assignee) => (
        <Avatar
          key={assignee.id}
          url={assignee.avatar}
          fullname={assignee.name}
          variants="xs"
          tooltip
          showBorder
        />
      ))}
    </AvatarGroup>
  </Box>
);

/** Trailing meta line: attendees • ticket id • origin • contact • category. */
const EventMeta = ({
  event,
  attendees,
}: {
  event: CalendarEvent;
  attendees?: CalendarEventPerson[];
}) => {
  const theme = useTheme();
  // A recurring ticket has no customer, so its board stands in for that slot.
  const originLabel = event.customerName ?? event.boardName;

  // Every event type carries a different subset of these, so the parts are
  // collected first and separated by bullets afterwards — that way a bullet
  // only ever lands between two parts that actually rendered.
  const parts: { key: string; node: ReactNode }[] = [];

  if (attendees?.length) {
    parts.push({
      key: "attendees",
      node: <AssigneeAvatars assignees={attendees} />,
    });
  }

  if (event.ticketId) {
    parts.push({ key: "ticketId", node: event.ticketId });
  }

  if (originLabel) {
    parts.push({ key: "origin", node: originLabel });
  }

  // if (event.contact) {
  //   parts.push({
  //     key: "contact",
  //     node: (
  //       <>
  //         {event.contact.isPrivileged ? (
  //           <img
  //             src={CrownIcon}
  //             alt="Privileged user"
  //             style={{
  //               width: "14px",
  //               height: "14px",
  //               verticalAlign: "middle",
  //               marginRight: "4px",
  //             }}
  //           />
  //         ) : null}
  //         {event.contact.name}
  //       </>
  //     ),
  //   });
  // }

  // if (event.category) {
  //   parts.push({ key: "category", node: event.category });
  // }

  // One truncating line rather than a flex row: the parts are inline, so the
  // ellipsis lands wherever the whole line runs out of room instead of every
  // part being clipped to its own share of the width. The container carries the
  // meta type style too — the ellipsis glyph is drawn by the container, so it
  // would otherwise render in the inherited body font.
  return (
    <Typography
      component="div"
      variant="body-xsmall"
      sx={{
        color: theme.palette.lightBg.low,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {parts.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 ? (
            <Box component="span" sx={{ mx: "4px" }}>
              •
            </Box>
          ) : null}
          {part.node}
        </Fragment>
      ))}
    </Typography>
  );
};

interface EventCardProps {
  event: CalendarEvent;
}

export const EventCard = ({ event }: EventCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const timezone = useGlobalStore((state) => state.timezone);
  const start = mspInstant(event.startAt, timezone);
  const barColor = getEventTypeColor(event, theme);
  const attendees = getAttendees(event);
  // Only tickets have somewhere to go — every other event type is inert.
  const ticketMappingId =
    event.eventType === "ticket" ? event.ticketMappingId : undefined;

  const openTicket = ticketMappingId
    ? () => {
        trackAiHome("calendar_event_clicked", {
          event_id: ticketMappingId,
          event_type: event.eventType,
          source: "home_calendar_panel",
        });
        navigate(`/app/service-desk/tickets/${ticketMappingId}`);
      }
    : undefined;

  // A recurring occurrence has no ticket to open yet — clicking it opens the
  // scheduled-ticket popover (skip / generate now) instead.
  const recurringTicketId =
    event.eventType === "recurring-ticket"
      ? event.recurringTicketId
      : undefined;

  const isInteractive = Boolean(openTicket || recurringTicketId);

  const card = (
    <Stack
      direction="row"
      alignItems="stretch"
      gap="12px"
      role={openTicket ? "button" : undefined}
      tabIndex={openTicket ? 0 : undefined}
      onClick={openTicket}
      onKeyDown={
        openTicket
          ? (keyEvent) => {
              if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                keyEvent.preventDefault();
                openTicket();
              }
            }
          : undefined
      }
      sx={{
        p: "8px 12px",
        backgroundColor: theme.palette.neutral.main,
        cursor: isInteractive ? "pointer" : "default",
        borderRadius: "8px",
        "&:hover": isInteractive
          ? { backgroundColor: theme.palette.neutral["005"] }
          : undefined,
      }}
    >
      <Stack
        sx={{
          width: "30px",
          flexShrink: 0,
          justifyContent: "center",

          // alignItems: "center",
        }}
      >
        <Typography
          variant="button-label"
          sx={{ color: theme.palette.lightBg.high, textAlign: "right" }}
        >
          {start.format("hh:mm")}
        </Typography>
        <Typography
          variant="body-xsmall"
          sx={{ color: theme.palette.lightBg.low, textAlign: "right" }}
        >
          {start.format("A")}
        </Typography>
      </Stack>

      <Box
        aria-hidden="true"
        sx={{
          width: "2px",
          borderRadius: "2px",
          flexShrink: 0,
          backgroundColor: barColor,
        }}
      />

      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="button-label"
          sx={{
            color: theme.palette.lightBg.high,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {event.title}
        </Typography>

        <EventMeta event={event} attendees={attendees} />
      </Stack>
    </Stack>
  );

  if (!recurringTicketId) return card;

  return (
    <Popover
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      paperSx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        mt: "4px",
      }}
      popoverContent={(closePopover) => (
        <ScheduledTicketModal
          onClose={closePopover}
          recurringTicketId={recurringTicketId}
          title={event.title}
          scheduledAtIso={event.startAt}
          description={event.description}
        />
      )}
    >
      {card}
    </Popover>
  );
};
